import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sellerOnboardingService, StripeAccountStatus, SellerInfo } from '../services/sellerOnboardingService';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export const SellerOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState<StripeAccountStatus | null>(null);
  const [sellerInfo, setSellerInfo] = useState<SellerInfo | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [formData, setFormData] = useState({
    stripeAccountId: '', // User-provided Stripe account ID
    businessName: '',
    businessType: 'individual',
    businessRegistrationNumber: '',
    businessAddressLine1: '',
    businessAddressLine2: '',
    businessCity: '',
    businessPostalCode: '',
    businessCountry: 'GB',
    businessTaxId: '',
    bankAccountHolderName: '',
    bankAccountSortCode: '',
    bankAccountNumber: '',
    bankAccountIban: '',
    bankAccountBic: '',
  });
  const [linkAccountMode, setLinkAccountMode] = useState(false);

  useEffect(() => {
    if (user?.role !== 'SELLER') {
      navigate('/dashboard');
      return;
    }
    loadAccountStatus();
    loadSellerInfo();
  }, [user, navigate]);

  const loadAccountStatus = async () => {
    try {
      const status = await sellerOnboardingService.getStripeAccountStatus();
      setAccountStatus(status);
    } catch (err: any) {
      console.error('Error loading account status:', err);
    }
  };

  const loadSellerInfo = async () => {
    try {
      const info = await sellerOnboardingService.getSellerInfo();
      setSellerInfo(info);
      // Pre-fill form if info exists
      if (info) {
        setFormData({
          stripeAccountId: info.stripeAccountId || '',
          businessName: info.businessName || '',
          businessType: info.businessType || 'individual',
          businessRegistrationNumber: info.businessRegistrationNumber || '',
          businessAddressLine1: info.businessAddressLine1 || '',
          businessAddressLine2: info.businessAddressLine2 || '',
          businessCity: info.businessCity || '',
          businessPostalCode: info.businessPostalCode || '',
          businessCountry: info.businessCountry || 'GB',
          businessTaxId: info.businessTaxId || '',
          bankAccountHolderName: info.bankAccountHolderName || '',
          bankAccountSortCode: info.bankAccountSortCode || '',
          bankAccountNumber: info.bankAccountNumber || '',
          bankAccountIban: info.bankAccountIban || '',
          bankAccountBic: info.bankAccountBic || '',
        });
      }
    } catch (err: any) {
      console.error('Error loading seller info:', err);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveBusinessInfo = async () => {
    setIsSavingInfo(true);
    setError('');
    try {
      // If user provided Stripe account ID, link it first
      if (linkAccountMode && formData.stripeAccountId) {
        try {
          await sellerOnboardingService.linkStripeAccount(formData.stripeAccountId);
          setSuccess('Stripe account linked successfully!');
          await loadAccountStatus(); // Refresh account status
        } catch (linkError: any) {
          // Handle different error formats
          let errorMessage = 'Failed to link Stripe account';
          
          if (linkError.message) {
            errorMessage = linkError.message;
          } else if (linkError.response?.data?.message) {
            errorMessage = linkError.response.data.message;
          } else if (linkError.response?.data?.error) {
            errorMessage = linkError.response.data.error;
          } else if (typeof linkError === 'string') {
            errorMessage = linkError;
          }
          
          throw new Error(errorMessage);
        }
      }

      // Save business information (excluding stripeAccountId from update)
      const { stripeAccountId, ...businessInfo } = formData;
      await sellerOnboardingService.updateSellerInfo(businessInfo);
      setSuccess('Information saved successfully!');
      await loadSellerInfo();
      setShowBusinessForm(false);
      setLinkAccountMode(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || err.response?.data?.message || 'Failed to save information. Please try again.');
    } finally {
      setIsSavingInfo(false);
    }
  };


  const handleCreateAccount = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await sellerOnboardingService.createStripeAccount();
      setSuccess('Stripe account created! Redirecting to onboarding...');
      
      // Update account status immediately
      await loadAccountStatus();
      
      // Small delay to ensure state is updated before redirect
      setTimeout(() => {
        // Redirect to Stripe onboarding
        if (result.onboardingUrl) {
          window.location.href = result.onboardingUrl;
        } else {
          setError('No onboarding URL received. Please try again.');
          setLoading(false);
        }
      }, 500);
    } catch (err: any) {
      console.error('Error creating Stripe account:', err);
      let errorMessage = 'Failed to create Stripe account. Please try again.';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await sellerOnboardingService.createOnboardingLink();
      window.location.href = result.url;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create onboarding link. Please try again.');
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!accountStatus?.accountId) {
      return (
        <div className="flex items-center gap-2 text-yellow-600">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span className="font-medium">Not Started</span>
        </div>
      );
    }

    if (accountStatus.status === 'ENABLED' && accountStatus.onboardingCompleted) {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircleIcon className="h-5 w-5" />
          <span className="font-medium">Active</span>
        </div>
      );
    }

    if (accountStatus.status === 'REJECTED') {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <XCircleIcon className="h-5 w-5" />
          <span className="font-medium">Rejected</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 text-yellow-600">
        <ExclamationTriangleIcon className="h-5 w-5" />
        <span className="font-medium">In Progress</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Seller Account Setup</h1>
          <p className="mt-2 text-gray-600">
            Complete your Stripe Connect account to receive payments from product sales
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <XCircleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium mb-1">Error</p>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="ml-3 text-red-400 hover:text-red-600 transition-colors"
                aria-label="Dismiss error"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        <Card className="mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Stripe Connect Status</h2>
              {getStatusBadge()}
            </div>

            {!accountStatus?.accountId ? (
              // No account - show create button
              <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg">
                <div className="text-center">
                  <BanknotesIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Set Up Payment Account
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Create your Stripe Connect account to start receiving payments. You'll get 90% of each sale, with 10% going to the platform.
                  </p>
                  <Button
                    onClick={handleCreateAccount}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
                  >
                    {loading ? 'Creating Account...' : 'Create Stripe Account'}
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-sm text-gray-600 mt-4">
                    This will create a Stripe Connect Express account and redirect you to complete onboarding.
                  </p>
                </div>
              </div>
            ) : accountStatus && (
              <div className="space-y-3 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Account ID:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-900">
                      {accountStatus.accountId || 'Not created'}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Onboarding Completed:</span>
                  <span className={accountStatus.onboardingCompleted ? 'text-green-600' : 'text-yellow-600'}>
                    {accountStatus.onboardingCompleted ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Charges Enabled:</span>
                  <span className={accountStatus.chargesEnabled ? 'text-green-600' : 'text-yellow-600'}>
                    {accountStatus.chargesEnabled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payouts Enabled:</span>
                  <span className={accountStatus.payoutsEnabled ? 'text-green-600' : 'text-yellow-600'}>
                    {accountStatus.payoutsEnabled ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            )}

            {/* Onboarding status */}
            {accountStatus?.accountId && !accountStatus.onboardingCompleted && (
              <div className="mt-6 space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm mb-2">
                    <strong>Complete Onboarding to Start Receiving Payments</strong>
                  </p>
                  <p className="text-yellow-700 text-sm mb-3">
                    You need to complete your Stripe onboarding to enable payments. You'll need to provide:
                  </p>
                  <ul className="text-yellow-700 text-sm mt-2 space-y-1 list-disc list-inside">
                    <li>Business information (name, type, registration number)</li>
                    <li>Business address and tax information</li>
                    <li>Bank account details for payouts</li>
                  </ul>
                  <div className="mt-3 p-3 bg-white rounded border border-yellow-200">
                    <p className="text-yellow-900 text-xs font-medium mb-1">💡 Note:</p>
                    <p className="text-yellow-800 text-xs">
                      In test mode, you can use test data for all fields. Stripe accepts test business information and test bank account details for testing purposes.
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm mb-2">
                    <strong>Complete Onboarding:</strong>
                  </p>
                  <p className="text-blue-700 text-sm mb-3">
                    Complete your Stripe onboarding to enable payments. Click below to continue.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={handleContinueOnboarding}
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? 'Loading...' : 'Continue Onboarding'}
                      <ArrowRightIcon className="ml-2 h-5 w-5" />
                    </Button>
                    {accountStatus.accountId && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          window.open(`https://dashboard.stripe.com/connect/accounts/${accountStatus.accountId}/onboarding`, '_blank');
                        }}
                        className="flex-1"
                      >
                        Open Stripe Dashboard
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {accountStatus?.status === 'ENABLED' && accountStatus.onboardingCompleted && (
              <div className="mt-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">
                    ✓ Your Stripe account is active and ready to receive payments!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    You'll receive 90% of each sale, with 10% going to the platform.
                  </p>
                </div>
              </div>
            )}

            {accountStatus?.status && accountStatus.status !== 'ENABLED' && accountStatus.status !== 'PENDING' && (
              <div className="mt-6">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800">
                    Your account is being reviewed. Please check back later or contact support if this takes longer than expected.
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Business Information Form/Display */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Business & Bank Information</h2>
              {!showBusinessForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBusinessForm(true)}
                >
                  {sellerInfo?.businessName ? 'Edit Information' : 'Add Information'}
                </Button>
              )}
            </div>

            {showBusinessForm ? (
              <div className="space-y-6">
                {/* Stripe Account ID Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Stripe Account Information</h3>
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                    <p className="text-yellow-800 text-sm mb-2">
                      <strong>Important:</strong> If you have created your own Stripe Connect account, enter your account ID here.
                    </p>
                    <p className="text-yellow-700 text-xs">
                      You can find your Stripe account ID in your Stripe Dashboard → Settings → Account details.
                      The account ID starts with "acct_".
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Account ID
                    </label>
                    <Input
                      value={formData.stripeAccountId}
                      onChange={(e) => handleFormChange('stripeAccountId', e.target.value)}
                      placeholder="acct_1234567890"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty if you want us to create an account for you
                    </p>
                  </div>
                </div>

                {/* Business Information Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                    Business Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <Input
                        value={formData.businessName}
                        onChange={(e) => handleFormChange('businessName', e.target.value)}
                        placeholder="ABC Care Services Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type *
                      </label>
                      <Select
                        value={formData.businessType}
                        onChange={(e) => handleFormChange('businessType', e.target.value)}
                        options={[
                          { value: 'individual', label: 'Individual' },
                          { value: 'company', label: 'Company' },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Number
                      </label>
                      <Input
                        value={formData.businessRegistrationNumber}
                        onChange={(e) => handleFormChange('businessRegistrationNumber', e.target.value)}
                        placeholder="12345678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax ID / VAT Number
                      </label>
                      <Input
                        value={formData.businessTaxId}
                        onChange={(e) => handleFormChange('businessTaxId', e.target.value)}
                        placeholder="GB123456789"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 1
                      </label>
                      <Input
                        value={formData.businessAddressLine1}
                        onChange={(e) => handleFormChange('businessAddressLine1', e.target.value)}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 2
                      </label>
                      <Input
                        value={formData.businessAddressLine2}
                        onChange={(e) => handleFormChange('businessAddressLine2', e.target.value)}
                        placeholder="Suite 100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <Input
                        value={formData.businessCity}
                        onChange={(e) => handleFormChange('businessCity', e.target.value)}
                        placeholder="London"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postal Code
                      </label>
                      <Input
                        value={formData.businessPostalCode}
                        onChange={(e) => handleFormChange('businessPostalCode', e.target.value)}
                        placeholder="SW1A 1AA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <Input
                        value={formData.businessCountry}
                        onChange={(e) => handleFormChange('businessCountry', e.target.value)}
                        placeholder="GB"
                      />
                    </div>
                  </div>
                </div>

                {/* Bank Information Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <BanknotesIcon className="h-5 w-5 mr-2" />
                    Bank Account Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Holder Name
                      </label>
                      <Input
                        value={formData.bankAccountHolderName}
                        onChange={(e) => handleFormChange('bankAccountHolderName', e.target.value)}
                        placeholder="ABC Care Services Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sort Code (UK)
                      </label>
                      <Input
                        value={formData.bankAccountSortCode}
                        onChange={(e) => handleFormChange('bankAccountSortCode', e.target.value)}
                        placeholder="12-34-56"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Number
                      </label>
                      <Input
                        value={formData.bankAccountNumber}
                        onChange={(e) => handleFormChange('bankAccountNumber', e.target.value)}
                        placeholder="12345678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IBAN
                      </label>
                      <Input
                        value={formData.bankAccountIban}
                        onChange={(e) => handleFormChange('bankAccountIban', e.target.value)}
                        placeholder="GB82WEST12345698765432"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        BIC / SWIFT Code
                      </label>
                      <Input
                        value={formData.bankAccountBic}
                        onChange={(e) => handleFormChange('bankAccountBic', e.target.value)}
                        placeholder="WESTGB22"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBusinessForm(false);
                      loadSellerInfo(); // Reset form
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveBusinessInfo}
                    disabled={isSavingInfo || !formData.businessName}
                    loading={isSavingInfo}
                  >
                    Save Information
                  </Button>
                </div>
              </div>
            ) : (
              // Display Mode
              sellerInfo && (sellerInfo.businessName || sellerInfo.bankAccountHolderName) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sellerInfo.businessName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Business Name</label>
                      <p className="mt-1 text-gray-900">{sellerInfo.businessName}</p>
                    </div>
                  )}
                  {sellerInfo.businessType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Business Type</label>
                      <p className="mt-1 text-gray-900 capitalize">{sellerInfo.businessType}</p>
                    </div>
                  )}
                  {sellerInfo.businessRegistrationNumber && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Registration Number</label>
                      <p className="mt-1 text-gray-900">{sellerInfo.businessRegistrationNumber}</p>
                    </div>
                  )}
                  {sellerInfo.businessTaxId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tax ID / VAT</label>
                      <p className="mt-1 text-gray-900">{sellerInfo.businessTaxId}</p>
                    </div>
                  )}
                  {(sellerInfo.businessAddressLine1 || sellerInfo.businessCity) && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Business Address</label>
                      <p className="mt-1 text-gray-900">
                        {[
                          sellerInfo.businessAddressLine1,
                          sellerInfo.businessAddressLine2,
                          sellerInfo.businessCity,
                          sellerInfo.businessPostalCode,
                          sellerInfo.businessCountry,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  )}
                  {sellerInfo.bankAccountHolderName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Bank Account Holder</label>
                      <p className="mt-1 text-gray-900">{sellerInfo.bankAccountHolderName}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No business information saved yet.</p>
                  <p className="text-sm mt-2">Click "Add Information" to enter your business and bank details.</p>
                </div>
              )
            )}
          </div>
        </Card>

        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};



